/**
 * ShareContainer - Right-aligned utilities container for InfoHUD
 */

import { createIconButton } from "@UI/IconButton/index.js";
import "./ShareContainer.scss";
import type { HandednessStateAPI } from "@/state/handedness";

export interface ShareContainerConfig {
  readonly onRefresh?: () => void;
  readonly handedness?: HandednessStateAPI | null;
}

export interface ShareContainerAPI {
  readonly getElement: () => HTMLElement;
  readonly destroy: () => void;
}

export const createShareContainer = (
  config: ShareContainerConfig = {}
): ShareContainerAPI => {
  let isDestroyed = false;
  let unsubscribeHandedness: (() => void) | null = null;

  const root = document.createElement("div");
  root.className = "share-container-component";

  const handednessState = config.handedness ?? null;

  const handednessToggle = document.createElement("button");
  handednessToggle.type = "button";
  handednessToggle.className = "share-container__handedness";
  handednessToggle.setAttribute("aria-label", "Toggle handedness");

  const updateHandednessButton = (value: "left" | "right") => {
    handednessToggle.textContent = value === "left" ? "L" : "R";
    handednessToggle.dataset.handedness = value;
    handednessToggle.title =
      value === "left" ? "Left-handed layout" : "Right-handed layout";
  };

  handednessToggle.addEventListener("click", () => {
    if (isDestroyed || !handednessState) return;
    const current = handednessState.get();
    const next = current === "left" ? "right" : "left";
    handednessState.set(next, { source: "shareContainer" });
  });

  if (handednessState) {
    updateHandednessButton(handednessState.get());
    if (typeof handednessState.subscribe === "function") {
      unsubscribeHandedness = handednessState.subscribe((value) => {
        if (value === "left" || value === "right") {
          updateHandednessButton(value);
        }
      });
    }
  } else {
    updateHandednessButton("right");
    handednessToggle.disabled = true;
    handednessToggle.title = "Handedness controls unavailable";
  }

  // Refresh button for iPad webapp mode
  const refreshButton = createIconButton({
    icon: "preview_refresh",
    size: 32 as any, // temporary until IconButton adds 32 type
    iconSize: 28,
    className: "share-container__refresh",
    onClick: () => {
      if (isDestroyed) return;
      if (config.onRefresh) {
        config.onRefresh();
      } else {
        // Default behavior: reload page
        window.location.reload();
      }
    },
  });

  // Color override to dark grey via CSS var
  root.style.setProperty("color", "var(--color-dark-grey)");
  root.appendChild(handednessToggle);
  root.appendChild(refreshButton.getElement());

  const destroy = (): void => {
    if (isDestroyed) return;
    isDestroyed = true;
    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }
    handednessToggle.remove();
    refreshButton.destroy();
    root.remove();
  };

  return {
    getElement: () => root,
    destroy,
  };
};

export const createShareContainerInContainer = (
  container: HTMLElement,
  config: ShareContainerConfig = {}
): ShareContainerAPI => {
  const sc = createShareContainer(config);
  container.appendChild(sc.getElement());
  return sc;
};
